/**
 * Convert PreparedDrop / PresetItem to preset item format
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  normalizeScheduleTemplateWallClockIso,
  scheduleDayOffsetFromWorkDate,
  toClickIdStructure,
  toDomainScheme,
} from '@trafficpulse/shared';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { filterSenderNamesMax11 } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';

import type { PresetItem, PresetItemPayload } from './types';

/** Normalize PresetItem (from API) to PresetItemPayload (null → undefined). */
export function presetItemToPayload(item: PresetItem): PresetItemPayload {
  const { id: _id, presetId: _p, orderIndex: _o, scheduleTemplateAt: stAt, ...rest } = item;
  const out: PresetItemPayload = {
    ...rest,
    campaignWiserId: rest.campaignWiserId ?? undefined,
    batchAmount: rest.batchAmount ?? undefined,
    batchDelay: rest.batchDelay ?? undefined,
    aiOptimizationType: rest.aiOptimizationType === 'ROI' || rest.aiOptimizationType === 'CTR'
      ? rest.aiOptimizationType
      : undefined,
    aiOptimizationBatchAmount: rest.aiOptimizationBatchAmount ?? undefined,
    aiOptimizationMinimalValuePercent: rest.aiOptimizationMinimalValuePercent ?? undefined,
    aiOptimizationSendUntil: rest.aiOptimizationSendUntil ?? undefined,
    staticTagId: rest.staticTagId ?? undefined,
    senderNames: rest.senderNames ?? undefined,
    smsTexts: rest.smsTexts ?? undefined,
    rotators: rest.rotators ?? undefined,
    scheduleTemplateDayOffset: rest.scheduleTemplateDayOffset ?? undefined,
  };
  if (stAt?.trim()) out.scheduleTemplateAt = stAt.trim();
  return out;
}

export type EditableItemFromDrop = PresetItemPayload & { id: string };

export function preparedDropToEditableItem(d: PreparedDrop): EditableItemFromDrop {
  const payload: PresetItemPayload = {
    routeId: d.routeId,
    campaignWiserId: d.wiserId,
    leadsListWiserId: d.leadsListWiserId,
    shortLinkTldValue: d.shortLinkTldValue,
    amount: d.amount,
    batchAmount: d.batchAmount ?? undefined,
    batchDelay: d.batchDelay ?? undefined,
    forceSend: d.forceSend ?? false,
    aiOptimizationEnabled: d.aiOptimizationEnabled ?? false,
    aiOptimizationType:
      d.aiOptimizationType === 'ROI' || d.aiOptimizationType === 'CTR'
        ? d.aiOptimizationType
        : undefined,
    aiOptimizationBatchAmount: d.aiOptimizationBatchAmount ?? undefined,
    aiOptimizationMinimalValuePercent: d.aiOptimizationMinimalValuePercent ?? undefined,
    aiOptimizationSendUntil: d.aiOptimizationSendUntil ?? undefined,
    useOnlyNewDomains: d.useOnlyNewDomains ?? false,
    clickIdStructure: toClickIdStructure(d.clickIdStructure),
    domainScheme: toDomainScheme(d.domainScheme),
    staticTagId: d.staticTagId ?? undefined,
    senderNames: filterSenderNamesMax11(d.senderNames ?? []),
    smsTexts: d.smsTexts ?? undefined,
    rotators: d.rotators ?? undefined,
  };
  if (d.executeAfter?.trim()) {
    payload.scheduleTemplateAt = normalizeScheduleTemplateWallClockIso(d.executeAfter.trim());
    // The normalised time keeps only the wall clock, so the day gap has to be
    // recorded separately or a late send loses its next-day placement the
    // moment it becomes a preset.
    payload.scheduleTemplateDayOffset = scheduleDayOffsetFromWorkDate(
      d.workDate,
      d.executeAfter.trim(),
    );
  }
  return { ...payload, id: `drop-${d.id}` };
}

export function preparedDropToPresetItemPayload(d: PreparedDrop): PresetItemPayload {
  const { id: _id, ...rest } = preparedDropToEditableItem(d);
  return rest;
}
